
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Star, Clock, Play, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GAME_LEVELS, LEVEL_THEMES } from '@/data/gameData';
import { useGameData } from '@/hooks/use-game-data';

interface LevelSelectorProps {
  onLevelSelect: (levelId: number, gridId: number) => void;
  onClose: () => void;
}

const LevelSelector = ({ onLevelSelect, onClose }: LevelSelectorProps) => {
  const { t } = useTranslation();
  const { gameData } = useGameData();

  const isLevelUnlocked = (levelId: number) => {
    if (levelId === 1) return true;
    return gameData.unlockedLevels?.includes(levelId) || false;
  };

  const getLevelProgress = (levelId: number) => {
    const completedGrids = gameData.completedGrids || {};
    const level = GAME_LEVELS.find(l => l.id === levelId);
    if (!level) return { percentage: 0, completed: 0, total: 0 };
    
    const totalGrids = level.grids.length;
    const completed = level.grids.filter(grid => 
      completedGrids[`${levelId}-${grid.id}`]
    ).length;
    
    return {
      percentage: Math.round((completed / totalGrids) * 100),
      completed,
      total: totalGrids
    };
  };

  const getNextIncompleteGrid = (levelId: number) => {
    const completedGrids = gameData.completedGrids || {};
    const level = GAME_LEVELS.find(l => l.id === levelId);
    if (!level) return level?.grids[0];

    // Find the first incomplete grid
    const incompleteGrid = level.grids.find(grid => 
      !completedGrids[`${levelId}-${grid.id}`]
    );

    // If all grids are completed, return the first grid (to restart)
    return incompleteGrid || level.grids[0];
  };

  const isLevelCompleted = (levelId: number) => {
    const progress = getLevelProgress(levelId);
    return progress.completed === progress.total && progress.total > 0;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          {t('game.selectLevel')}
        </h2>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 max-h-[60vh] overflow-y-auto">
        {GAME_LEVELS.map((level) => {
          const isUnlocked = isLevelUnlocked(level.id);
          const progress = getLevelProgress(level.id);
          const theme = LEVEL_THEMES[level.theme as keyof typeof LEVEL_THEMES];
          const nextGrid = getNextIncompleteGrid(level.id);
          const completed = isLevelCompleted(level.id);
          const hasProgress = progress.completed > 0;

          return (
            <Card 
              key={level.id}
              className={`${isUnlocked ? 'cursor-pointer hover:shadow-md' : 'opacity-50'} transition-all`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`
                      w-12 h-12 rounded-full bg-gradient-to-r ${theme.color} 
                      flex items-center justify-center text-white text-xl
                    `}>
                      {isUnlocked ? theme.icon : <Lock size={20} />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm">{t(`game.levels.${level.theme}`)}</h3>
                      <div className="flex items-center space-x-2 text-xs text-gray-600">
                        <Clock size={12} />
                        <span>{level.timeLimit}s</span>
                        {progress.completed > 0 && (
                          <>
                            <Star size={12} className="text-yellow-500" />
                            <span>{progress.completed}/{progress.total}</span>
                          </>
                        )}
                      </div>
                      {hasProgress && nextGrid && !completed && (
                        <div className="text-xs text-purple-600 font-medium mt-1">
                          Reprendre: Grille {nextGrid.id}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isUnlocked && (
                    <div className="text-right space-y-2">
                      <div className="text-xs text-gray-500">
                        {level.grids.length} {t('game.grids')}
                      </div>
                      
                      {/* Main action button */}
                      <Button
                        size="sm"
                        className={`bg-gradient-to-r ${theme.color} text-white min-w-[80px]`}
                        onClick={() => onLevelSelect(level.id, nextGrid?.id || level.grids[0].id)}
                      >
                        {completed ? (
                          <>
                            <RotateCcw size={14} className="mr-1" />
                            Recommencer
                          </>
                        ) : hasProgress ? (
                          <>
                            <Play size={14} className="mr-1" />
                            Continuer
                          </>
                        ) : (
                          <>
                            <Play size={14} className="mr-1" />
                            Commencer
                          </>
                        )}
                      </Button>

                      {/* Option to restart if has progress */}
                      {hasProgress && !completed && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full text-xs"
                          onClick={() => onLevelSelect(level.id, level.grids[0].id)}
                        >
                          <RotateCcw size={12} className="mr-1" />
                          Recommencer
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {isUnlocked && progress.completed > 0 && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Progression</span>
                      <span>{progress.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`bg-gradient-to-r ${theme.color} h-2 rounded-full transition-all`}
                        style={{ width: `${progress.percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default LevelSelector;
